import { toast } from 'sonner';
import { showErrorToast, type ShowErrorToastOptions } from '@/shared/components/feedback/ErrorToast';
import { isApiErrorEnvelope, type ApiErrorEnvelope } from '@/shared/types/apiError';

export interface NotifyApi {
  success: (message: string) => void;
  warning: (message: string) => void;
  info: (message: string) => void;
  /**
   * Show an error notification.
   * - String input → simple sonner error toast (legacy call sites).
   * - ApiErrorEnvelope input → routed via showErrorToast with severity/duration honored.
   */
  error: {
    (message: string): void;
    (envelope: ApiErrorEnvelope, opts: ShowErrorToastOptions): void;
  };
}

const errorImpl = (
  input: string | ApiErrorEnvelope,
  opts?: ShowErrorToastOptions
): void => {
  if (typeof input === 'string') {
    toast.error(input);
    return;
  }

  if (isApiErrorEnvelope(input) && opts) {
    showErrorToast(input, opts);
    return;
  }

  // Defensive fallback if a caller passes an envelope without opts.
  if (isApiErrorEnvelope(input)) {
    toast.error(input.userMessage, { description: input.nextStep ?? undefined });
    return;
  }

  toast.error('An error occurred');
};

// Stable singleton — toast/showErrorToast are module-level, so the API can be
// shared across all callers. Returning the same reference each call lets
// consumers safely include `notify` in useEffect/useCallback deps.
const notifyApi: NotifyApi = {
  success: (message: string) => {
    toast.success(message);
  },
  error: errorImpl as NotifyApi['error'],
  warning: (message: string) => {
    toast.warning(message);
  },
  info: (message: string) => {
    toast.info(message);
  },
};

export const useNotification = (): NotifyApi => notifyApi;
